import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUsers, FiPieChart, FiTrendingUp, FiTrendingDown } = FiIcons;

const CustomerVarianceTable = () => {
  // Mock Data for Customer Wise Variance
  const customerData = [
    {
      id: 1,
      customerName: 'CVS',
      affiliation: 'RedOak Sourcing',
      groupName: 'Abiraterone Acetate',
      sku: "250mg 120's count",
      ndc: '72005003092',
      status: 'Primary',
      forecastedVol: 13000,
      forecast10Day: 4333,
      forecast20Day: 8667,
      actualVol1to10: 3126,
      actualVol11to20: 5136,
      actualVol21to30: 0,
      budgetedVol: 17002 // Assumed for Budget vs Actual calc
    },
    {
      id: 2,
      customerName: 'McKesson',
      affiliation: 'ClarusOne',
      groupName: 'Abiraterone Acetate',
      sku: "500mg 60's count",
      ndc: '72005028560',
      status: 'Secondary',
      forecastedVol: 900,
      forecast10Day: 300,
      forecast20Day: 600,
      actualVol1to10: 432,
      actualVol11to20: 240,
      actualVol21to30: 0,
      budgetedVol: 1004
    },
    {
      id: 3,
      customerName: 'Walgreens',
      affiliation: 'WBA',
      groupName: 'Eltrombopag',
      sku: "25mg 30's count",
      ndc: '72005012345',
      status: 'Primary',
      forecastedVol: 4800,
      forecast10Day: 1600,
      forecast20Day: 3200,
      actualVol1to10: 1500,
      actualVol11to20: 1600,
      actualVol21to30: 1800,
      budgetedVol: 5000
    },
    {
      id: 4,
      customerName: 'Cardinal Health',
      affiliation: 'RedOak Sourcing',
      groupName: 'Mirabegron',
      sku: "50mg 30's count",
      ndc: '72005044556',
      status: 'Primary',
      forecastedVol: 7500,
      forecast10Day: 2500,
      forecast20Day: 5000,
      actualVol1to10: 2500,
      actualVol11to20: 2600,
      actualVol21to30: 2800,
      budgetedVol: 8000
    },
    {
      id: 5,
      customerName: 'AmerisourceBergen',
      affiliation: 'Walgreens Boots Alliance',
      groupName: 'Venetoclax',
      sku: "100mg 120's count",
      ndc: '72005099887',
      status: 'Secondary',
      forecastedVol: 1100,
      forecast10Day: 366,
      forecast20Day: 733,
      actualVol1to10: 350,
      actualVol11to20: 400,
      actualVol21to30: 380,
      budgetedVol: 1200
    },
    {
      id: 6,
      customerName: 'Rite Aid',
      affiliation: 'McKesson',
      groupName: 'Brivaracetam',
      sku: "10mg 60's count",
      ndc: '72005011223',
      status: 'Primary',
      forecastedVol: 1900,
      forecast10Day: 633,
      forecast20Day: 1266,
      actualVol1to10: 600,
      actualVol11to20: 650,
      actualVol21to30: 700,
      budgetedVol: 2000
    }
  ];

  const processedData = customerData.map(item => {
    const totalActual = item.actualVol1to10 + item.actualVol11to20 + item.actualVol21to30;
    const budgetVariance = item.budgetedVol > 0 ? ((totalActual - item.budgetedVol) / item.budgetedVol) * 100 : 0;
    const forecastVariance = item.forecastedVol > 0 ? ((totalActual - item.forecastedVol) / item.forecastedVol) * 100 : 0;

    return {
      ...item,
      totalActual,
      budgetVariance,
      forecastVariance
    };
  });

  // KPIs
  const totalForecast = processedData.reduce((acc, curr) => acc + curr.forecastedVol, 0);
  const totalActual = processedData.reduce((acc, curr) => acc + curr.totalActual, 0);
  const avgForecastVariance = processedData.reduce((acc, curr) => acc + curr.forecastVariance, 0) / processedData.length;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { 
            label: 'Total Forecasted Volume', 
            value: totalForecast.toLocaleString(), 
            icon: FiPieChart, 
            color: 'text-purple-600', 
            bg: 'bg-purple-50' 
          },
          { 
            label: 'Total Actual Sold', 
            value: totalActual.toLocaleString(), 
            icon: FiUsers, 
            color: 'text-blue-600', 
            bg: 'bg-blue-50' 
          },
          { 
            label: 'Avg Forecast Variance', 
            value: `${avgForecastVariance.toFixed(2)}%`, 
            icon: avgForecastVariance >= 0 ? FiTrendingUp : FiTrendingDown, 
            color: avgForecastVariance >= 0 ? 'text-green-600' : 'text-red-600', 
            bg: avgForecastVariance >= 0 ? 'bg-green-50' : 'bg-red-50' 
          }
        ].map((kpi, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kpi.bg}`}>
                <SafeIcon icon={kpi.icon} className={kpi.color} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{kpi.label}</p>
                <p className="text-xl font-bold text-gray-900">{kpi.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Customer Wise Variance Report</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Customer Name</th>
                <th className="px-4 py-3 font-semibold">Affiliation Group</th>
                <th className="px-4 py-3 font-semibold">Product Group</th>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">NDC</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Forecasted Vol<br/>(30 Days)</th>
                <th className="px-4 py-3 font-semibold text-right">10 Days<br/>Forecast</th>
                <th className="px-4 py-3 font-semibold text-right">20 Days<br/>Forecast</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Vol<br/>Day 1-10</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Vol<br/>Day 11-20</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Vol<br/>Day 21-30</th>
                <th className="px-4 py-3 font-semibold text-right">Total Actual</th>
                <th className="px-4 py-3 font-semibold text-right">Risk (F vs A)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {processedData.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{item.customerName}</td>
                  <td className="px-4 py-3 text-gray-700">{item.affiliation}</td>
                  <td className="px-4 py-3 text-gray-700">{item.groupName}</td>
                  <td className="px-4 py-3 text-gray-700">{item.sku}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono">{item.ndc}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      item.status === 'Primary' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{item.forecastedVol.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-500 text-xs">{item.forecast10Day.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-500 text-xs">{item.forecast20Day.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol1to10.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol11to20.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol21to30.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-medium text-blue-600">{item.totalActual.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-medium ${item.forecastVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.forecastVariance.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerVarianceTable;